import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import LoadingSpinner from '@/components/LoadingSpinner';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

/**
 * AuthCallback Component
 *
 * Handles Supabase authentication callbacks for:
 * - Email confirmations
 * - Password resets
 * - Magic link sign-ins
 * - OAuth callbacks
 *
 * This component processes the authentication tokens from the URL
 * and redirects the user to the appropriate page.
 */
export default function AuthCallback() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your authentication...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('[AUTH-CALLBACK] Processing authentication callback...');

        // Get the current URL hash and search params
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);

        // Check for error in URL
        const error = hashParams.get('error') || searchParams.get('error');
        const errorDescription = hashParams.get('error_description') ||
                                 searchParams.get('error_description');

        if (error) {
          console.error('[AUTH-CALLBACK] Error in callback:', error, errorDescription);
          setStatus('error');
          setMessage(errorDescription || 'Authentication failed. Please try again.');

          // Redirect to sign-in page after 3 seconds
          setTimeout(() => {
            setLocation('/sign-in?error=auth_callback_failed');
          }, 3000);
          return;
        }

        // Supabase's client library will automatically detect and process the session
        // from the URL if detectSessionInUrl is enabled (which it is in supabase.ts)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[AUTH-CALLBACK] Error getting session:', sessionError);
          setStatus('error');
          setMessage('Failed to authenticate. Please try signing in again.');

          setTimeout(() => {
            setLocation('/sign-in?error=session_failed');
          }, 3000);
          return;
        }

        if (session) {
          console.log('[AUTH-CALLBACK] ✅ Authentication successful:', session.user.email);
          setStatus('success');
          setMessage('Email confirmed successfully! Redirecting to your journey...');

          // Store session info in localStorage (backup)
          localStorage.setItem('supabase_session', JSON.stringify(session));

          // Redirect to the journey page
          setTimeout(() => {
            setLocation('/journey');
          }, 2000);
        } else {
          console.warn('[AUTH-CALLBACK] No session found in callback');
          setStatus('error');
          setMessage('No authentication session found. Please try signing in.');

          setTimeout(() => {
            setLocation('/sign-in?error=no_session');
          }, 3000);
        }
      } catch (error: any) {
        console.error('[AUTH-CALLBACK] Exception during callback:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try signing in again.');

        setTimeout(() => {
          setLocation('/sign-in?error=callback_exception');
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [setLocation]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && <LoadingSpinner />}
            {status === 'success' && (
              <CheckCircle2 className="h-16 w-16 text-green-500 animate-pulse" />
            )}
            {status === 'error' && (
              <XCircle className="h-16 w-16 text-red-500" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === 'loading' && 'Processing...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Authentication Failed'}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center text-sm text-gray-500">
          {status === 'loading' && (
            <p>Please wait while we confirm your email address...</p>
          )}
          {status === 'success' && (
            <p>You will be redirected to your Red Helicopter journey shortly.</p>
          )}
          {status === 'error' && (
            <div className="space-y-2">
              <p>If this problem persists, please contact support.</p>
              <a
                href="/sign-in"
                className="text-rh-helicopter-red hover:underline font-medium inline-block mt-2"
              >
                Return to Sign In
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
